import React, { useState, Fragment } from "react";
import {
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import PropTypes from "prop-types";
import clsx from "clsx";
import { deleteLeadDocument, getLeadDocumentPreviewUrl } from "services/leadsService";
import { deleteJobDocument, getJobDocumentPreviewUrl } from "services/jobsService";
import { DOCUMENT_ENTITY_TYPES } from "services/documentFolderService";
import { downloadDocumentWithToast } from "components/leads/LeadDetails/DocumentsTab/utils/documentUtils";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { toast } from "sonner";
import { canEditLead } from "permissions/permissionHelpers";
import TableActionMenu from "custom-components/shared/TableActionMenu";

export function DocumentActions({
  document,
  entityType,
  refetch,
  useCase = "card",
  refreshProgressJourney = () => { },
  onVerificationAction,
  IsFinalApproved = false,
  onView,
  onRename = null,
  onMove = null,
  isStudent = false,
  readOnly = false,
}) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const hasVerificationAccess = document?.canApproveOrReject;
  const isJobDoc = entityType === DOCUMENT_ENTITY_TYPES.JOB;

  const handleViewDocument = () => {
    onView?.();
  };

  const openPreviewUrl = async (documentId) => {
    const result = isJobDoc
      ? await getJobDocumentPreviewUrl(documentId)
      : await getLeadDocumentPreviewUrl(documentId);
    if (result.success && result.data?.url) {
      window.open(result.data.url, "_blank");
    } else {
      toast.error(result.error || "Failed to open document");
    }
  };

  const handleDownloadDocument = async () => {
    return downloadDocumentWithToast(document, { entityType });
  };

  const handleDeleteDocument = () => {
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (document.id) {
      try {
        const result = isJobDoc
          ? await deleteJobDocument(document.id)
          : await deleteLeadDocument(document.id);
        if (result?.success) {
          toast.success("Document deleted successfully.");
          setDeleteModalOpen(false);
          await refreshProgressJourney();
          refetch();
        } else {
          toast.error(result?.error || result?.message || "Failed to delete document.");
        }
      } catch (error) {
        toast.error("Error deleting document.");
        console.error("Delete document error:", error);
      }
    }
  };

  const showApproveOption = document?.verificationStatus === 0 || document?.verificationStatus === 2;
  const showRejectOption = document?.verificationStatus === 0 || document?.verificationStatus === 1;

  const handleVerificationAction = (event, action) => {
    if (event?.stopPropagation) event.stopPropagation();
    if (typeof onVerificationAction === "function") {
      onVerificationAction(document, action);
    }
  };

  const customActions = [
    {
      key: "open-tab",
      label: "Open in New Tab",
      icon: ArrowTopRightOnSquareIcon,
      iconColor: "text-indigo-500",
      onClick: () => openPreviewUrl(document.id),
    },
  ];

  if (canEditLead()) {
    customActions.push({
      key: "download",
      label: "Download",
      icon: ArrowDownTrayIcon,
      iconColor: "text-blue-500",
      onClick: handleDownloadDocument,
    });
  }

  if (onMove && canEditLead() && !IsFinalApproved && !isStudent && !readOnly && !document?.isAdditionalDocument) {
    customActions.push({
      key: "move-item",
      label: "Move To...",
      icon: ArrowRightIcon,
      iconColor: "text-indigo-500",
      onClick: () => onMove(document),
    });
  }

  if (onRename && canEditLead() && !IsFinalApproved && !isStudent && !readOnly && !document?.isAdditionalDocument) {
    customActions.push({
      key: "rename",
      label: "Rename",
      icon: PencilIcon,
      iconColor: "text-amber-500",
      onClick: () => onRename(document),
    });
  }

  if (hasVerificationAccess && showApproveOption) {
    customActions.push({
      key: "approve",
      label: "Approve",
      icon: CheckCircleIcon,
      iconColor: "text-emerald-500",
      onClick: (item, e) => handleVerificationAction(e, "approve"),
    });
  }

  if (hasVerificationAccess && showRejectOption) {
    customActions.push({
      key: "reject",
      label: "Reject",
      icon: XCircleIcon,
      iconColor: "text-red-500",
      danger: true,
      onClick: (item, e) => handleVerificationAction(e, "reject"),
    });
  }

  return (
    <>
      <TableActionMenu
        item={document}
        onView={handleViewDocument}
        onDelete={handleDeleteDocument}
        canDelete={canEditLead() && !IsFinalApproved && !isStudent && !readOnly && !document?.isAdditionalDocument}
        viewLabel="View"
        deleteLabel="Delete"
        customActions={customActions}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onOk={handleConfirmDelete}
        state="pending"
        messages={{
          pending: {
            title: "Delete Document",
            description: `Are you sure you want to delete "${document?.name}"? This action cannot be undone.`,
            actionText: "Delete",
          },
          success: {
            title: "Document Deleted",
            description: "The document has been successfully deleted.",
            actionText: "Done",
          },
          error: {
            title: "Delete Failed",
            description: "Failed to delete the document. Please try again.",
            actionText: "Retry",
          },
        }}
        buttonColor="error"
      />
    </>
  );
}

export default DocumentActions;
